import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePowerSyncDb } from '../adapters/powersync';
import { useInitialSyncMilestones, type InitialSyncMilestones } from './useInitialSyncMilestones';
import { useUploadQueueStats, type UploadQueueInfo } from './useUploadQueueStats';
import { retrySync, forceResync } from './syncActions';

// ─── Browser Support ───

export interface BrowserSupport {
  supported: boolean;
  missing: string[];
  browserName: string;
}

function detectBrowserName(): string {
  const ua = navigator?.userAgent ?? '';
  if (ua.includes('Firefox/')) {
    const v = ua.match(/Firefox\/([\d.]+)/)?.[1];
    return `Firefox ${v ?? ''}`.trim();
  }
  if (ua.includes('Edg/')) {
    const v = ua.match(/Edg\/([\d.]+)/)?.[1];
    return `Edge ${v ?? ''}`.trim();
  }
  if (ua.includes('OPR/') || ua.includes('Opera/')) {
    const v = ua.match(/(?:OPR|Opera)\/([\d.]+)/)?.[1];
    return `Opera ${v ?? ''}`.trim();
  }
  if (ua.includes('Chrome/')) {
    const v = ua.match(/Chrome\/([\d.]+)/)?.[1];
    return `Chrome ${v ?? ''}`.trim();
  }
  if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const v = ua.match(/Version\/([\d.]+)/)?.[1];
    return `Safari ${v ?? ''}`.trim();
  }
  return ua.slice(0, 80) || 'Unknown browser';
}

function detectBrowserSupport(): BrowserSupport {
  const missing: string[] = [];
  if (typeof Worker === 'undefined') missing.push('Web Workers');
  if (typeof SharedWorker === 'undefined') missing.push('SharedWorker');
  return { supported: missing.length === 0, missing, browserName: detectBrowserName() };
}

// ─── Types ───

export interface SyncGateState {
  phase: 'idle' | 'initial-sync' | 'ready' | 'recovery';
  /** Milestone data from useInitialSyncMilestones */
  sync: InitialSyncMilestones;
  /** Upload queue info for force-resync safety gate */
  uploadQueue: UploadQueueInfo;
  /** Skip the gate manually */
  skip: () => void;
  /** Non-destructive reconnect */
  retrySync: () => Promise<void>;
  /** Destructive clear + reload */
  forceResync: () => Promise<void>;
  /** Whether a retry/force is in progress */
  isRetrying: boolean;
  /** Browser capability info */
  browserSupport: BrowserSupport;
  /** PowerSync init error (from provider) */
  syncError: Error | null;
}

/** After this many ms with incomplete milestones, enter recovery mode */
const WATCHDOG_TIMEOUT_MS = 90_000;

// ─── Context ───

const SyncGateContext = createContext<SyncGateState | null>(null);

export function useSyncGate(): SyncGateState {
  const ctx = useContext(SyncGateContext);
  if (!ctx) throw new Error('useSyncGate must be used within SyncGateProvider');
  return ctx;
}

// ─── Provider ───

export function SyncGateProvider({ children }: { children: ReactNode }) {
  const { db, connector, error } = usePowerSyncDb();
  const browserSupport = useMemo(() => detectBrowserSupport(), []);
  const sync = useInitialSyncMilestones();
  const uploadQueue = useUploadQueueStats();

  const [phase, setPhase] = useState<SyncGateState['phase']>('idle');
  const [isRetrying, setIsRetrying] = useState(false);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Phase transitions based on milestone completion ───
  useEffect(() => {
    if (phase === 'ready') return; // Already done

    if (sync.allComplete) {
      console.log('[SyncGate] All milestones met — transitioning to ready');
      setPhase('ready');
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      return;
    }

    // If db is available and milestones aren't complete, we're syncing
    if (db && phase === 'idle') {
      setPhase('initial-sync');
    }
  }, [db, sync.allComplete, phase]);

  // ─── Watchdog: enter recovery mode after WATCHDOG_TIMEOUT_MS ───
  useEffect(() => {
    if (phase !== 'initial-sync') return;

    watchdogRef.current = setTimeout(() => {
      setPhase((prev) => {
        if (prev === 'ready') return prev;
        console.warn('[SyncGate] Watchdog timeout — entering recovery mode');
        return 'recovery';
      });
    }, WATCHDOG_TIMEOUT_MS);

    return () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
  }, [phase]);

  // Recovery mode stays until user takes action (Retry or Force Resync)
  // or milestones are eventually met. No auto-skip.

  // ─── Fallback: no db but browser supported → recovery mode ───
  useEffect(() => {
    if (db) return;
    if (phase === 'ready' || phase === 'recovery') return;
    if (!browserSupport.supported) return;

    const fallback = setTimeout(() => {
      setPhase((prev) => {
        if (prev === 'ready' || prev === 'recovery') return prev;
        console.warn('[SyncGate] Fallback timeout (no db) — entering recovery mode');
        return 'recovery';
      });
    }, WATCHDOG_TIMEOUT_MS);

    return () => clearTimeout(fallback);
  }, [db, phase, browserSupport.supported]);

  // ─── Actions ───

  const skip = useCallback(() => {
    console.log('[SyncGate] Manual skip');
    setPhase('ready');
  }, []);

  const handleRetrySync = useCallback(async () => {
    console.log('[SyncGate] handleRetrySync called', { hasDb: !!db, hasConnector: !!connector });
    if (!db || !connector) {
      // DB never initialized — a page reload is the simplest retry
      console.log('[SyncGate] No db instance — reloading page to retry init');
      window.location.reload();
      return;
    }
    setIsRetrying(true);
    try {
      await retrySync(db, connector);
      // Give sync 5s to pull data, then re-check milestones
      await new Promise((r) => setTimeout(r, 5_000));
      // Phase will auto-transition if milestones are met via the useEffect above
    } catch (err) {
      console.error('[SyncGate] Retry sync failed:', err);
    } finally {
      setIsRetrying(false);
    }
  }, [db, connector]);

  const handleForceResync = useCallback(async () => {
    console.log('[SyncGate] handleForceResync called', { hasDb: !!db });
    setIsRetrying(true);
    if (!db) {
      // DB never initialized — clear OPFS/IDB manually and reload
      console.log('[SyncGate] No db instance — clearing storage and reloading');
      try {
        // Clear IndexedDB databases matching powersync
        const dbs = await indexedDB.databases();
        for (const dbInfo of dbs) {
          if (dbInfo.name && dbInfo.name.toLowerCase().includes('powersync')) {
            console.log('[SyncGate] Deleting IndexedDB:', dbInfo.name);
            indexedDB.deleteDatabase(dbInfo.name);
          }
        }
        // Clear schema version so it re-inits cleanly
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('powercareai:ps-schema-version')) {
            localStorage.removeItem(key);
          }
        }
      } catch (err) {
        console.error('[SyncGate] Manual storage clear failed:', err);
      }
      window.location.reload();
      return;
    }
    try {
      await forceResync(db);
      // forceResync calls window.location.reload() — won't reach here
    } catch (err) {
      console.error('[SyncGate] Force resync failed:', err);
      setIsRetrying(false);
    }
  }, [db]);

  const value: SyncGateState = {
    phase,
    sync,
    uploadQueue,
    skip,
    retrySync: handleRetrySync,
    forceResync: handleForceResync,
    isRetrying,
    browserSupport,
    syncError: error,
  };

  return <SyncGateContext.Provider value={value}>{children}</SyncGateContext.Provider>;
}
